import React, { useState } from "react";
import { Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";

const POSTS = [
  {
    slug: "together-we-rise", title: "TOGETHER WE RISE", order: 1,
    caption: `"Rednecks, bikers, brothers — we'll never hide." 🤠🇺🇸

Original country from the backroads. Aurora shot in my own front yard. Streaming everywhere now.

🎧 everythangredneck.com

#CountryMusic #IndieCountry #OutlawCountry #RednecksBikersBrothers #EverythangRedneck #NewCountry #TogetherWeRise #AmericanaMusic #CountryRock #BikerLife`,
  },
  {
    slug: "redneck-rich", title: "REDNECK RICH", order: 2,
    caption: `"Loyalty wrapped in leather and steel." 🥃

Real wealth ain't in the dollar signs. It's in the sunsets, the dirt roads, and the ones who ride with you.

🎧 everythangredneck.com

#CountryMusic #IndieCountry #RedneckRich #EverythangRedneck #OutlawCountry #AmericanaMusic #NewCountry #CountryLife #WorkingClass #BackroadsUSA`,
  },
  {
    slug: "crimson-conquerors", title: "CRIMSON CONQUERORS", order: 3,
    caption: `"Stand tall, brothers and sisters — loyalty in our veins." ⚔️🔥

The rally cry for the ones who refuse to break. Refuse to hide.

🎧 everythangredneck.com

#CountryMusic #OutlawCountry #CrimsonConquerors #EverythangRedneck #IndieCountry #AmericanaMusic #NewCountry #CountryRock #VeteransSupport #StandTall`,
  },
  {
    slug: "redneck-rebirth", title: "REDNECK REBIRTH", order: 4,
    caption: `"She wants to be a redneck, with heart and soul." 🌅

A country love song about walking away from the city lights toward dirt roads, fireflies, and gold-painted sunsets.

🎧 everythangredneck.com

#CountryMusic #IndieCountry #RedneckRebirth #CountryLove #EverythangRedneck #AmericanaMusic #NewCountry #StorySongs #CountryGirl #BackroadsUSA`,
  },
  {
    slug: "united-we-ride", title: "UNITED WE RIDE", order: 5,
    caption: `"Brothers side by side — through the fire and the rain." 🏍️🇺🇸

Freedom calls. And we ride together. Every mile.

🎧 everythangredneck.com

#CountryMusic #BikerLife #HarleyDavidson #UnitedWeRide #EverythangRedneck #OutlawCountry #Motorcycle #BikerBrotherhood #NewCountry #IndieCountry`,
  },
  {
    slug: "crimson-brotherhood", title: "CRIMSON BROTHERHOOD", order: 6,
    caption: `"Blood-bound loyalty. Iron-forged spirit." ⚡

For the ones you ride or die with. Nothing louder than that.

🎧 everythangredneck.com

#CountryMusic #OutlawCountry #CrimsonBrotherhood #EverythangRedneck #Brotherhood #BikerLife #IndieCountry #AmericanaMusic #NewCountry #RideOrDie`,
  },
  {
    slug: "redneck-revolution", title: "REDNECK REVOLUTION", order: 7,
    caption: `"The people rise. Takin' back what's ours." 🇺🇸🔥

An anthem for every forgotten hometown, every working hand, every neighbor left behind.

🎧 everythangredneck.com

#CountryMusic #RedneckRevolution #OutlawCountry #EverythangRedneck #WeThePeople #WorkingClass #IndieCountry #AmericanaMusic #NewCountry #StandUp`,
  },
  {
    slug: "warriors-of-the-sunrise", title: "WARRIORS OF THE SUNRISE", order: 8,
    caption: `"Every dawn hard-fought. Every day earned." 🌄⚔️

Sunrise as the battle line. This one's for the ones who wake up and keep going.

🎧 everythangredneck.com

#CountryMusic #WarriorsOfTheSunrise #EverythangRedneck #Veterans #OutlawCountry #IndieCountry #AmericanaMusic #NewCountry #FirstResponders #NeverQuit`,
  },
];

function PostCard({ post }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(post.caption);
      setCopied(true);
      toast.success(`${post.title} caption copied — go paste it in TikTok/Reels/FB!`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for older browsers / iOS quirks
      const ta = document.createElement("textarea");
      ta.value = post.caption;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      toast.success("Caption copied!");
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="bg-[#151A14] border-2 border-[#2C332A] p-5" data-testid={`post-${post.slug}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#DD5422] font-anton">Day {post.order}</div>
          <h2 className="font-anton text-2xl uppercase tracking-tight leading-none">{post.title}</h2>
        </div>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-4 items-start">
        <div className="bg-[#0A0E0B] border-2 border-[#DD5422] overflow-hidden aspect-[9/16] max-w-[220px] mx-auto md:mx-0">
          <video
            src={`/api/videos/${post.slug}-short.mp4`}
            controls
            playsInline
            preload="metadata"
            data-testid={`post-video-${post.slug}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="bg-[#0A0E0B] border-2 border-[#2C332A] p-3 text-sm whitespace-pre-wrap text-[#B5BFB0] leading-relaxed max-h-52 overflow-auto">
            {post.caption}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={copy}
              data-testid={`copy-caption-${post.slug}`}
              className={`flex items-center justify-center gap-2 py-4 font-anton uppercase tracking-widest text-sm border-2 transition-colors ${
                copied
                  ? "bg-[#4CAF50] text-[#0A0E0B] border-[#4CAF50]"
                  : "bg-[#DD5422] text-[#0A0E0B] border-[#DD5422] hover:bg-[#FF6B35]"
              }`}
            >
              {copied ? <><Check className="w-5 h-5" strokeWidth={3} /> Copied!</> : <><Copy className="w-5 h-5" strokeWidth={2.5} /> Copy Caption</>}
            </button>
            <a
              href={`/api/videos/${post.slug}-short.mp4?download=1`}
              download
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`save-video-${post.slug}`}
              className="flex items-center justify-center gap-2 py-4 font-anton uppercase tracking-widest text-sm border-2 border-[#DD5422] text-[#DD5422] hover:bg-[#DD5422] hover:text-[#0A0E0B]"
            >
              <Download className="w-5 h-5" strokeWidth={2.5} /> Save Video
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Post() {
  return (
    <div className="min-h-screen max-w-4xl mx-auto px-4 md:px-8 py-10" data-testid="post-page">
      <div className="mb-8">
        <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-2">/ Social Media Kit</div>
        <h1 className="font-anton text-4xl md:text-6xl uppercase tracking-tight leading-none">One-Tap Posting.</h1>
        <p className="text-[#8D9688] mt-4 max-w-2xl">
          Each song ready to go. Tap <strong className="text-[#DD5422]">COPY CAPTION</strong> → open TikTok/Reels/Facebook → paste. That&apos;s it. No highlighting, no thumbs, no headaches.
        </p>
      </div>

      <div className="bg-[#151A14] border-2 border-[#DD5422] p-4 mb-6 text-sm text-[#B5BFB0]" data-testid="post-instructions">
        <div className="font-anton uppercase tracking-widest text-[#DD5422] text-xs mb-2">How to post one</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Tap <strong className="text-[#DD5422]">COPY CAPTION</strong> on the song you want to post.</li>
          <li>Tap <strong className="text-[#DD5422]">SAVE VIDEO</strong> — pick &ldquo;Save Video&rdquo; when iOS asks.</li>
          <li>Open TikTok / Instagram / Facebook → new post → pick that video from your camera roll.</li>
          <li>In the caption box, long-press and tap <strong className="text-[#DD5422]">Paste</strong>. Boom.</li>
          <li>Repeat for the next 2 platforms with the SAME video and SAME caption.</li>
        </ol>
      </div>

      <div className="space-y-4">
        {POSTS.map((p) => <PostCard key={p.slug} post={p} />)}
      </div>
    </div>
  );
}
